import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, ArrowLeftIcon, ArrowRightIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useTraining } from '../../contexts/TrainingContext';
import ProgressTracker from '../../components/training/ProgressTracker';
import QuestionCard from '../../components/training/QuestionCard';

export default function OnboardingTrainingPage() {
  const navigate = useNavigate();
  const {
    questions,
    currentStage,
    setCurrentStage,
    answers,
    updateAnswer,
    submitStage,
    isLoading,
    error,
    progress,
    getStageQuestions,
    getStageProgress,
    getOverallProgress
  } = useTraining();

  const [showFeedback, setShowFeedback] = useState(false);
  const [stageComplete, setStageComplete] = useState(false);
  
  const stageQuestions = getStageQuestions(currentStage);
  const totalStages = Math.max(...questions.map(q => q.stage), 1);
  const isLastStage = currentStage >= totalStages;
  const allQuestionsAnswered = stageQuestions.every(q => answers[q.id] !== undefined && answers[q.id] !== '');

  const handleSubmit = async () => {
    if (!allQuestionsAnswered) return;
    
    if (!showFeedback) {
      // First submit - show feedback
      setShowFeedback(true);
      setStageComplete(true);
      return;
    }
    
    // Submit to server and move to next stage
    try {
      await submitStage();
      setShowFeedback(false);
      setStageComplete(false);
    } catch (err) {
      console.error('Error submitting stage:', err);
    }
  };

  const handleNextStage = () => {
    setCurrentStage(prev => Math.min(prev + 1, totalStages));
    setShowFeedback(false);
    setStageComplete(false);
  };

  const handlePrevStage = () => {
    setCurrentStage(prev => Math.max(prev - 1, 1));
    setShowFeedback(false);
    setStageComplete(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading training content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Something went wrong</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (progress?.completed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100">
            <CheckCircleIcon className="h-10 w-10 text-green-600" />
          </div>
          <h2 className="mt-4 text-2xl font-bold text-gray-900">Training Complete!</h2>
          <p className="mt-2 text-gray-600">
            You've successfully completed all training modules. Well done!
          </p>
          <div className="mt-6">
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Agent Onboarding Training</h1>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-500 hover:text-gray-700"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 transition-all duration-300"
                style={{ width: `${getOverallProgress()}%` }}
              />
            </div>
            <div className="mt-1 text-sm text-gray-500 text-right">
              {Math.round(getOverallProgress())}% Complete
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stage Progress */}
        <div className="mb-8">
          <h2 className="text-lg font-medium text-gray-900 mb-2">
            Stage {currentStage} of {totalStages}
          </h2>
          <div className="flex items-center">
            <div className="flex-1 bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${getStageProgress(currentStage)}%` }}
              />
            </div>
            <span className="ml-2 text-sm font-medium text-gray-700">
              {getStageProgress(currentStage)}%
            </span>
          </div>
        </div>

        {/* Progress Tracker */}
        <ProgressTracker
          currentStage={currentStage}
          totalStages={totalStages}
          onStageSelect={(stage) => {
            if (stage < currentStage) {
              setCurrentStage(stage);
              setShowFeedback(false);
              setStageComplete(false);
            }
          }}
          progress={progress}
        />

        {/* Questions */}
        <div className="space-y-6 mb-8">
          {stageQuestions.map((question) => (
            <QuestionCard
              key={question.id}
              question={question}
              value={answers[question.id]}
              onChange={updateAnswer}
              showFeedback={showFeedback}
              disabled={stageComplete}
            />
          ))}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handlePrevStage}
            disabled={currentStage <= 1}
            className={`inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md ${
              currentStage <= 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Previous
          </button>
          
          <div className="flex items-center space-x-4">
            {stageComplete && !isLastStage && (
              <button
                type="button"
                onClick={handleNextStage}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Next Stage
                <ArrowRightIcon className="h-4 w-4 ml-2" />
              </button>
            )}
            
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!allQuestionsAnswered || (stageComplete && isLastStage)}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                !allQuestionsAnswered || (stageComplete && isLastStage)
                  ? 'bg-blue-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
            >
              {isLastStage && stageComplete ? 'Completed' : stageComplete ? 'Continue' : 'Check Answers'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
